import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-wca-red text-white border-transparent shadow-[0_6px_16px_-6px_rgba(196,18,48,0.45)] hover:bg-wca-red-dark hover:shadow-[0_9px_22px_-6px_rgba(196,18,48,0.55)]',
  secondary: 'bg-wca-surface border-wca-red text-wca-red hover:bg-wca-red-light',
  ghost:
    'bg-transparent border-transparent text-wca-gray-mid hover:text-wca-black hover:bg-wca-gray-light',
};

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-5',
  lg: 'h-12 px-7 text-[17px]',
};

/**
 * Brand button. Pill-shaped (rounded-full) for the premium coffee-brand feel;
 * primary carries a soft red glow and a subtle press scale.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition-all duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...props}
    />
  );
});
